import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Archive, Copy, Edit, Eye, PauseCircle, Play, PlayCircle, Plus, Search, Trash2, Zap } from 'lucide-react';
import DataTableWithQuery, { QueryParams as TableQueryParams } from '@/components/ui/DataTableWithQuery';
import Modal from '@/components/ui/Modal';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect';
import Button from '@/components/ui/Button';
import { TableAction, TableColumn } from '@/types';
import { AdCampaign, AdCampaignStatus, CreateAdCampaignRequest } from '@/types/ads';
import { useAdCampaigns, useUpdateAdCampaignStatus, useCreateAdCampaign, useDeleteAdCampaign } from '@/hooks/useAdCampaigns';
import AdCampaignStatusBadge from '@/components/ui/AdCampaignStatusBadge';
import AdCampaignTypeBadges from '@/components/ui/AdCampaignTypeBadges';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { adCampaignService } from '@/services/adCampaignService';
import { ApiClientError } from '@/services/apiClient';
import DisplayNowModal from '@/components/ads/DisplayNowModal';

type Filters = {
  statusFilter?: 'ALL' | 'ACTIVE' | 'ARCHIVED';
};

type ContentPreview = {
  type: 'TEXT' | 'BANNER' | 'VIDEO' | 'NEWS_TICKER';
  campaign: AdCampaign;
};

const Campaigns = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirm, dialog } = useConfirmDialog();
  const [displayNowCampaign, setDisplayNowCampaign] = useState<AdCampaign | null>(null);
  const [contentPreview, setContentPreview] = useState<ContentPreview | null>(null);

  const statusFromQuery = searchParams.get('statusFilter');
  const statusFilter: Filters['statusFilter'] =
    statusFromQuery === 'ALL' || statusFromQuery === 'ACTIVE' || statusFromQuery === 'ARCHIVED'
      ? statusFromQuery
      : 'ACTIVE';

  const updateStatusMutation = useUpdateAdCampaignStatus();
  const createCampaignMutation = useCreateAdCampaign();
  const deleteCampaignMutation = useDeleteAdCampaign();

  const initialFilters = useMemo(() => {
    const mapped: Record<string, unknown> = {
      statusFilter: statusFilter && statusFilter !== 'ALL' ? statusFilter : undefined,
    };
    Object.keys(mapped).forEach((k) => mapped[k] === undefined && delete mapped[k]);
    return mapped;
  }, [statusFilter]);

  const updateStatusFilterParam = (value: Filters['statusFilter']) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!value || value === 'ACTIVE') {
      nextParams.delete('statusFilter');
    } else {
      nextParams.set('statusFilter', value);
    }
    setSearchParams(nextParams);
  };

  const columns: TableColumn<AdCampaign>[] = [
    {
      sortable: true,
      key: 'name',
      header: 'Nombre',
      accessor: (c) => (
        <button
          type="button"
          className="text-indigo-700 hover:underline font-medium"
          onClick={() => navigate(`/ads/campaigns/${c.id}`)}
        >
          {c.name}
        </button>
      ),
      className: 'min-w-64',
    },
    {
      sortable: true,
      key: 'status',
      header: 'Estado',
      accessor: (c) => <AdCampaignStatusBadge status={c.status} />,
      className: 'w-32',
    },
    {
      key: 'types',
      header: 'Tipo(s)',
      accessor: (c) => <AdCampaignTypeBadges campaign={c} />,
      className: 'min-w-40',
    },
    {
      key: 'content',
      header: 'Contenido',
      accessor: (c) => {
        if (c.type === 'TEXT') {
          const text = c.text?.trim();
          if (!text) return <span className="text-sm text-gray-500">—</span>;
          const shouldShowPreviewButton = text.length > 80;
          return (
            <div className="flex items-center gap-2 min-w-0 max-w-sm">
              <span className="text-sm text-gray-700 truncate">{text}</span>
              {shouldShowPreviewButton && (
                <button
                  type="button"
                  onClick={() => setContentPreview({ type: 'TEXT', campaign: c })}
                  className="inline-flex items-center text-indigo-600 hover:text-indigo-800"
                  aria-label="Ver texto completo"
                  title="Ver texto completo"
                >
                  <Eye className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        }

        if (c.type === 'NEWS_TICKER') {
          const firstNews = c.newsTickerItems?.[0] || c.newsTickerRssUrl || '';
          if (!firstNews) return <span className="text-sm text-gray-500">—</span>;
          return (
            <div className="flex items-center gap-2 min-w-0 max-w-sm">
              <span className="text-sm text-gray-700 truncate">{firstNews}</span>
              <button
                type="button"
                onClick={() => setContentPreview({ type: 'NEWS_TICKER', campaign: c })}
                className="inline-flex items-center text-indigo-600 hover:text-indigo-800"
                aria-label="Ver noticias"
                title="Ver noticias"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          );
        }

        if (c.type === 'BANNER') {
          if (!c.bannerImageUrl) return <span className="text-sm text-gray-500">—</span>;
          return (
            <button
              type="button"
              onClick={() => setContentPreview({ type: 'BANNER', campaign: c })}
              className="inline-block rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title="Ver banner"
              aria-label="Ver banner"
            >
              <img
                src={c.bannerImageUrl}
                alt={`Banner de ${c.name}`}
                className="h-10 w-24 object-cover rounded-md border border-gray-200"
              />
            </button>
          );
        }

        if (!c.videoUrl) return <span className="text-sm text-gray-500">—</span>;
        return (
          <button
            type="button"
            onClick={() => setContentPreview({ type: 'VIDEO', campaign: c })}
            className="inline-flex items-center text-blue-600 hover:text-blue-800"
            aria-label="Reproducir video"
            title="Reproducir video"
          >
            <Play className="h-4 w-4 cursor-pointer" />
          </button>
        );
      },
      className: 'min-w-52',
    },
    {
      sortable: true,
      key: 'priority',
      header: 'Prioridad',
      accessor: (c) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-blue-100 text-blue-800 border border-blue-200">
          {c.priority}
        </span>
      ),
      className: 'w-28',
    },
  ];

  const handleDuplicate = async (campaign: AdCampaign) => {
    try {
      const fresh = await adCampaignService.getAdCampaignById(campaign.id);
      const draft: CreateAdCampaignRequest = {
        name: `${fresh.name} (Copy)`,
        status: 'DRAFT',
        durationMode: fresh.durationMode ?? 'DATE_RANGE',
        startAt: fresh.startAt,
        endAt: fresh.endAt,
        priority: fresh.priority,
        weight: fresh.weight,
        videoMode: fresh.videoMode,
        videoPlacements: fresh.showOnVod && fresh.type === 'VIDEO' ? ['PRE_ROLL'] : undefined,
        adTagUrl: fresh.videoMode === 'NONE' ? undefined : fresh.adTagUrl?.trim(),
        videoUrl: fresh.videoUrl,
        showOnLiveTv: fresh.showOnLiveTv || false,
        showOnVod: fresh.showOnVod,
        showOnRadio: fresh.showOnRadio ?? false,
        radioDisplayMode: fresh.radioDisplayMode ?? 'BOTH',
        includeRadioStationIds: fresh.includeRadioStationIds ?? [],
        excludeRadioStationIds: fresh.excludeRadioStationIds ?? [],
        videoDismissible: fresh.videoDismissible,
        videoMinSecondsBeforeDismiss: fresh.videoMinSecondsBeforeDismiss,
        text: fresh.type === 'TEXT' ? fresh.text : undefined,
        textFontSize: fresh.type === 'TEXT' || fresh.type === 'NEWS_TICKER' ? fresh.textFontSize : undefined,
        textFontColor: fresh.type === 'TEXT' || fresh.type === 'NEWS_TICKER' ? fresh.textFontColor : undefined,
        textFontFamily: fresh.type === 'TEXT' || fresh.type === 'NEWS_TICKER' ? fresh.textFontFamily : undefined,
        textBackgroundColor: fresh.type === 'TEXT' || fresh.type === 'NEWS_TICKER' ? fresh.textBackgroundColor : undefined,
        textTickerSpeedSeconds: fresh.type === 'TEXT' || fresh.type === 'NEWS_TICKER' ? fresh.textTickerSpeedSeconds : undefined,
        newsTickerSourceType: fresh.type === 'NEWS_TICKER' ? fresh.newsTickerSourceType : undefined,
        newsTickerRssUrl: fresh.type === 'NEWS_TICKER' ? fresh.newsTickerRssUrl : undefined,
        newsTickerMaxItems: fresh.type === 'NEWS_TICKER' ? fresh.newsTickerMaxItems : undefined,
        newsTickerBackgroundImageUrl: fresh.type === 'NEWS_TICKER' ? fresh.newsTickerBackgroundImageUrl : undefined,
        newsTickerLeftImageUrl: fresh.type === 'NEWS_TICKER' ? fresh.newsTickerLeftImageUrl : undefined,
        newsTickerSeparator: fresh.type === 'NEWS_TICKER' ? fresh.newsTickerSeparator : undefined,
        newsTickerLoopEnabled: fresh.type === 'NEWS_TICKER' ? fresh.newsTickerLoopEnabled : undefined,
        newsTickerItems: fresh.type === 'NEWS_TICKER' ? fresh.newsTickerItems : undefined,
        bannerImageUrl: fresh.bannerImageUrl,
        type: fresh.type,
        showTimeInSeconds: fresh.showTimeInSeconds,
        bannerPosition:  fresh.bannerPosition,
        bannerScaleFactor: fresh.bannerScaleFactor,
        locationTargets: fresh.locationTargets,
        includeChannelIds: fresh.includeChannelIds,
        excludeChannelIds: fresh.excludeChannelIds,
        maxImpressionsPerUserPerDay: fresh.maxImpressionsPerUserPerDay,
      };
      const created = await createCampaignMutation.mutateAsync(draft);
      toast.success('Campaña duplicada');
      navigate(`/ads/campaigns/${created.id}/edit`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo duplicar');
    }
  };

  const handleSetStatus = async (campaign: AdCampaign, status: AdCampaignStatus) => {
    try {
      await updateStatusMutation.mutateAsync({ id: campaign.id, status, version: campaign.version });
      toast.success('Estado actualizado');
    } catch (error) {
      if (error instanceof ApiClientError && (error.status === 409 || error.status === 412)) {
        toast.error('La campaña cambió en el servidor. Refresca la lista e intenta de nuevo.');
      } else {
        toast.error(error instanceof Error ? error.message : 'No se pudo actualizar el estado');
      }
    }
  };

  const handleArchive = async (campaign: AdCampaign) => {
    const confirmed = await confirm({
      title: 'Archivar campaña',
      message: 'Esta campaña quedará archivada y no será elegible para selección.',
      confirmLabel: 'Archivar',
      cancelLabel: 'Cancelar',
      confirmVariant: 'danger',
      onConfirm: async () => handleSetStatus(campaign, 'ARCHIVED'),
    });

    if (!confirmed) return;
  };

  const handleDelete = async (campaign: AdCampaign) => {
    const confirmed = await confirm({
      title: 'Eliminar campaña',
      message: 'Esta acción eliminará la campaña de forma permanente.',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      confirmVariant: 'danger',
      onConfirm: async () => {
        await deleteCampaignMutation.mutateAsync(campaign.id);
      },
    });

    if (!confirmed) return;
    toast.success('Campaña eliminada');
  };

  const actions: TableAction<AdCampaign>[] = [
    {
      label: 'Editar',
      icon: Edit,
      onClick: (c) => navigate(`/ads/campaigns/${c.id}/edit`),
      className: 'text-gray-700 hover:text-indigo-600',
    },
    {
      label: 'Duplicar',
      icon: Copy,
      onClick: handleDuplicate,
      className: 'text-gray-700 hover:text-indigo-600',
    },
    {
      label: 'Pausar',
      icon: PauseCircle,
      onClick: (c) => handleSetStatus(c, 'PAUSED'),
      className: 'text-gray-700 hover:text-yellow-600',
      show: (c) => c.status === 'ACTIVE',
    },
    {
      label: 'Reanudar',
      icon: PlayCircle,
      onClick: (c) => handleSetStatus(c, 'ACTIVE'),
      className: 'text-gray-700 hover:text-green-600',
      show: (c) => c.status === 'PAUSED',
    },
    {
      label: 'Archivar',
      icon: Archive,
      onClick: handleArchive,
      className: 'text-red-600 hover:text-red-800',
      show: (c) => c.status !== 'ARCHIVED',
    },
    {
      label: 'Mostrar ahora',
      icon: Zap,
      onClick: (campaign) => {
        setDisplayNowCampaign(campaign);
      },
      className: 'text-indigo-700 hover:text-indigo-900',
      show: (c) => c.status === 'ACTIVE',
    },
    {
      label: 'Eliminar',
      icon: Trash2,
      onClick: handleDelete,
      className: 'text-red-600 hover:text-red-800',
    },
  ];

  const closeDisplayNowModal = () => {
    setDisplayNowCampaign(null);
  };

  const closeContentPreview = () => {
    setContentPreview(null);
  };

  const handleCreateCampaign = () => {
    navigate('/ads/campaigns/create');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <DataTableWithQuery
          showTotalCount
          tableStyle="compact"
          columns={columns}
          actions={actions}
          useQueryHook={useAdCampaigns as unknown as (params: TableQueryParams) => ReturnType<typeof useAdCampaigns>}
          onCreate={handleCreateCampaign}
          initialFilters={initialFilters}
          headerRenderer={({ searchQuery, onSearchChange, searchPlaceholder, totalCount }) => (
            <div className="border-b border-gray-200 bg-white">
              <div className="px-4 py-2 md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">Campañas de publicidad</h3>
                    <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                      {totalCount}
                    </span>
                  </div>
                  <Button onClick={handleCreateCampaign} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Crear <span className="hidden sm:inline">campaña</span>
                  </Button>
                </div>
                <div className="relative mt-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="mt-3">
                  <AutocompleteSelect
                    value={statusFilter}
                    onChange={(value) => updateStatusFilterParam(value as Filters['statusFilter'])}
                    options={[
                      { value: 'ACTIVE', label: 'Activos' },
                      { value: 'ALL', label: 'Todos' },
                      { value: 'ARCHIVED', label: 'Archivados' },
                    ]}
                    placeholder="Estado"
                    allowClear={false}
                    size="sm"
                    className="text-base"
                  />
                </div>
              </div>
              <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex">
                <h3 className="text-lg font-medium text-gray-900">Campañas de publicidad</h3>
                <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                  {totalCount}
                </span>
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="min-w-[180px]">
                  <AutocompleteSelect
                    value={statusFilter}
                    onChange={(value) => updateStatusFilterParam(value as Filters['statusFilter'])}
                    options={[
                      { value: 'ACTIVE', label: 'Activos' },
                      { value: 'ALL', label: 'Todos' },
                      { value: 'ARCHIVED', label: 'Archivados' },
                    ]}
                    placeholder="Estado"
                    allowClear={false}
                    size="sm"
                    className="text-base"
                  />
                </div>
                <div className="ml-auto">
                  <Button onClick={handleCreateCampaign} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Crear campaña
                  </Button>
                </div>
              </div>
            </div>
          )}
          title="Campañas de publicidad"
          searchPlaceholder="Buscar por nombre..."
          className="rounded-none shadow-none border border-gray-200"
        />

        <Modal
          isOpen={Boolean(contentPreview)}
          onClose={closeContentPreview}
          title={
            contentPreview
              ? contentPreview.type === 'TEXT'
                ? `Texto: ${contentPreview.campaign.name}`
                : contentPreview.type === 'BANNER'
                  ? `Banner: ${contentPreview.campaign.name}`
                  : contentPreview.type === 'NEWS_TICKER'
                    ? `Noticias: ${contentPreview.campaign.name}`
                    : `Video: ${contentPreview.campaign.name}`
              : 'Contenido'
          }
          size={contentPreview?.type === 'TEXT' || contentPreview?.type === 'NEWS_TICKER' ? 'lg' : 'xl'}
        >
          {contentPreview?.type === 'TEXT' && (
            <p className="whitespace-pre-wrap break-words text-sm text-gray-800">
              {contentPreview.campaign.text || '—'}
            </p>
          )}
          {contentPreview?.type === 'BANNER' && (
            contentPreview.campaign.bannerImageUrl ? (
              <img
                src={contentPreview.campaign.bannerImageUrl}
                alt={`Banner de ${contentPreview.campaign.name}`}
                className="w-full max-h-[75vh] object-contain rounded-lg"
              />
            ) : (
              <div className="text-sm text-gray-500">No hay banner para mostrar.</div>
            )
          )}
          {contentPreview?.type === 'NEWS_TICKER' && (
            <div className="space-y-3 text-sm text-gray-800">
              {contentPreview.campaign.newsTickerRssUrl && (
                <p><span className="font-medium">RSS:</span> {contentPreview.campaign.newsTickerRssUrl}</p>
              )}
              <ul className="space-y-2">
                {(contentPreview.campaign.newsTickerItems ?? []).map((item, index) => (
                  <li key={`${index}-${item}`} className="rounded-md border border-gray-200 p-2">
                    {item}
                  </li>
                ))}
              </ul>
              {(contentPreview.campaign.newsTickerItems ?? []).length === 0 && !contentPreview.campaign.newsTickerRssUrl && (
                <div className="text-sm text-gray-500">No hay noticias para mostrar.</div>
              )}
            </div>
          )}
          {contentPreview?.type === 'VIDEO' && (
            contentPreview.campaign.videoUrl ? (
              <video
                key={contentPreview.campaign.videoUrl}
                src={contentPreview.campaign.videoUrl}
                controls
                playsInline
                className="w-full rounded-lg bg-black max-h-[70vh]"
              />
            ) : (
              <div className="text-sm text-gray-500">No hay video para reproducir.</div>
            )
          )}
        </Modal>

        {displayNowCampaign && (
          <DisplayNowModal
            isOpen={Boolean(displayNowCampaign)}
            onClose={closeDisplayNowModal}
            campaignId={displayNowCampaign.id}
            campaignName={displayNowCampaign.name}
          />
        )}
        {dialog}
      </div>
    </div>
  );
};

export default Campaigns;
